/**
 * Admin Theme System
 *
 * Centralized design tokens that adapt to light/dark mode.
 * Shared across Store Command Center, System Core, User Management, etc.
 *
 * Usage:
 *   // In components (preferred — mode-aware):
 *   const { colors, cardStyle, elevatedInputStyle } = useAdminTheme();
 *
 *   // Static import (backward-compat, dark-mode only):
 *   import { AdminColors, cardStyle } from '../admin-theme';
 */

import { Box, styled } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DEFAULT_GROUND, FONT_DATA, GroundTokens, defaultGroundForMode, getGround } from './theme/grounds';
import { useThemeMode } from './context/ThemeModeContext';
import { useMemo } from 'react';

// ============================================================================
// Color Palettes
// ============================================================================

export type AdminColorTokens = {
    bgDeepest: string;
    bgCard: string;
    bgElevated: string;
    bgGlass: string;
    textPrimary: string;
    textSecondary: string;
    textMuted: string;
    border: string;
    borderGlow: string;
    success: string;
    successGlow: string;
    error: string;
    errorGlow: string;
    warning: string;
    warningGlow: string;
    info: string;
    infoGlow: string;
    purple: string;
    purpleGlow: string;
};

/**
 * Admin tokens are a VIEW over the ground (see ./theme/grounds.ts). The names
 * below predate the grounds and are kept so every page that reads
 * `colors.bgCard` etc. keeps working; the values come from the ground.
 */
export function adminColorsForGround(g: GroundTokens): AdminColorTokens {
    const dark = g.mode === 'dark';
    return {
        bgDeepest: g.canvas,
        bgCard: g.panel,
        bgElevated: g.raised,
        bgGlass: alpha(g.panel, 0.86),
        textPrimary: g.tx,
        textSecondary: g.tx2,
        textMuted: g.tx3,
        border: g.lineSoft,
        borderGlow: g.line,
        success: g.ok,
        successGlow: g.ok,
        error: g.crit,
        errorGlow: g.crit,
        warning: g.warn,
        warningGlow: g.warn,
        // `info` is what focus rings and hover borders read — the ember, in this skin.
        info: g.ember,
        infoGlow: g.ember,
        purple: dark ? '#A78BFA' : '#7C3AED',
        purpleGlow: dark ? '#A78BFA' : '#7C3AED',
    };
}

const darkColors: AdminColorTokens = adminColorsForGround(getGround(DEFAULT_GROUND));
const lightColors: AdminColorTokens = adminColorsForGround(getGround(defaultGroundForMode('light')));

/** Get mode-aware admin colors (the default ground of that mode). Prefer useAdminTheme(). */
export function getAdminColors(mode: 'light' | 'dark'): AdminColorTokens {
    return mode === 'light' ? lightColors : darkColors;
}

/** Static dark-mode colors (backward-compat — use useAdminTheme() for mode-aware) */
export const AdminColors = darkColors;

// ============================================================================
// Mode-Aware Style Builders
// ============================================================================

/**
 * Height of the app bar (CustomAppBar), which react-admin renders `position: fixed`.
 * Page headers must stick BELOW it — `top: 0` parks them underneath the app bar,
 * where they're invisible, which reads as "sticky is broken".
 */
export const APP_BAR_HEIGHT = 56;

function buildGlassHeaderStyle(c: AdminColorTokens, mode: 'light' | 'dark') {
    return {
        background: c.bgGlass,
        backdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${c.border}`,
        position: 'sticky' as const,
        top: APP_BAR_HEIGHT,
        // Above page content, below the fixed app bar (MUI AppBar sits at 1100).
        zIndex: 10,
    };
}

function buildCardStyle(c: AdminColorTokens) {
    return {
        bgcolor: c.bgCard,
        border: `1px solid ${c.border}`,
        borderRadius: 2,
    };
}

function buildElevatedInputStyle(c: AdminColorTokens) {
    return {
        '& .MuiInputBase-root': {
            bgcolor: c.bgElevated,
            color: c.textPrimary,
            transition: 'all 0.2s',
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: c.border,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: c.info,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: c.info,
                borderWidth: 2,
            },
        },
        '& .MuiInputLabel-root': {
            color: c.textSecondary,
            '&.Mui-focused': {
                color: c.info,
            },
        },
        '& .MuiSelect-icon': {
            color: c.textSecondary,
        },
        '& .MuiFormHelperText-root': {
            color: c.textMuted,
        },
    };
}

function buildAdminListStyle(c: AdminColorTokens) {
    const cs = buildCardStyle(c);
    return {
        bgcolor: c.bgDeepest,
        minHeight: '100vh',
        '& .RaList-main': { bgcolor: c.bgDeepest },
        '& .MuiPaper-root': { ...cs, boxShadow: 'none', color: c.textPrimary },
        '& .MuiToolbar-root': { bgcolor: c.bgCard, color: c.textPrimary },
        '& .MuiTablePagination-root': { color: c.textSecondary, borderTop: `1px solid ${c.border}` },
        '& .MuiIconButton-root': { color: c.textSecondary },
        '& .MuiTableHead-root .MuiTableCell-root': {
            bgcolor: c.bgCard, color: c.textMuted, fontWeight: 700, fontFamily: FONT_DATA,
            textTransform: 'uppercase', fontSize: '0.6875rem', letterSpacing: '0.13em',
            borderBottom: `1px solid ${c.border}`,
        },
        '& .MuiTableBody-root .MuiTableRow-root': { bgcolor: c.bgCard },
        '& .MuiTableBody-root .MuiTableRow-root:hover': { bgcolor: `${c.bgElevated} !important` },
        '& .MuiTableCell-root': { color: c.textPrimary, borderColor: c.border },
        '& .MuiCheckbox-root': { color: c.textMuted },
    };
}

// ============================================================================
// Static Exports (backward-compat — dark mode locked)
// ============================================================================

/** Glassmorphism header style (Sticky top) */
export const glassHeaderStyle = buildGlassHeaderStyle(darkColors, 'dark');

/** Standard Card/Panel style */
export const cardStyle = buildCardStyle(darkColors);

/** Elevated Input Style (for TextFields/Selects) */
export const elevatedInputStyle = buildElevatedInputStyle(darkColors);

/** React Admin List/Datagrid Styles */
export const adminListStyle = buildAdminListStyle(darkColors);

// ============================================================================
// Hook: useAdminTheme() — mode-aware (preferred)
// ============================================================================

/**
 * Returns mode-aware admin design tokens and style helpers.
 * Recomputes when the user cycles the ground.
 */
export function useAdminTheme() {
    const { mode, ground } = useThemeMode();
    return useMemo(() => {
        const colors = adminColorsForGround(ground);
        return {
            mode,
            colors,
            cardStyle: buildCardStyle(colors),
            glassHeaderStyle: buildGlassHeaderStyle(colors, mode),
            elevatedInputStyle: buildElevatedInputStyle(colors),
            adminListStyle: buildAdminListStyle(colors),
        };
    }, [mode, ground]);
}

// ============================================================================
// Helper Components
// ============================================================================

/** Pulsing Status Dot */
export const StatusDot = styled(Box)(({ color }: { color?: string }) => ({
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: color || darkColors.success,
    boxShadow: `0 0 8px ${color || darkColors.success}`,
}));
